import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Heart } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function WellnessInRelationships() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">WELLNESS IN RELATIONSHIPS</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Introduction</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Heart className="h-16 w-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">WELLNESS IN RELATIONSHIPS</h2>
            <p className="text-slate-300 leading-relaxed">
              A powerful shift in consciousness for creating health and well-being
            </p>
          </div>

          {/* Wellness Continuum Diagram */}
          <div className="bg-white rounded-lg p-6 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_belonging-sessions-1/artifacts/hizf8ynp_ChatGPT%20Image%20Nov%2015%2C%202025%20at%2002_02_02%20PM.png"
              alt="Illness/Wellness Continuum - From Treatment Model to High Level Wellness"
              className="w-full h-auto rounded"
              style={{ imageRendering: 'high-quality', maxWidth: '100%' }}
            />
          </div>

          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-700/30 rounded-lg p-8 mb-8">
            <p className="text-green-100 leading-relaxed text-lg mb-6">
              Learning to think in terms of wellness is a powerful shift in consciousness for creating health and well-being in all living systems. Much of our present mental health system focuses on trying to reduce symptoms. In the wellness model we are aiming for the <span className="font-semibold text-white">awareness, education, and growth that leads to HIGH LEVEL WELLNESS</span>.
            </p>
            <p className="text-green-200 leading-relaxed">
              All living systems rely on a constant flow of information about the well-being of each part in the system.
            </p>
          </div>

          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">Applying Wellness in Relationship Life</h3>
            <p className="text-blue-100 mb-4">Track what needs are met and unmet for each person, then:</p>
            <ul className="space-y-3 text-blue-100">
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                <span>Celebrating those met needs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                <span>Strategizing about ways to meet the unmet needs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                <span>Learning from conflicts about what needs are unmet for individuals in the community</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                <span>Strategizing about ways to meet those unmet needs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                <span>Tracking on the quality of relationships between all individuals in the community</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                <span>Strategizing about ways to improve the quality of relationships that are not optimal</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                <span>Meet regularly to discuss the level of need satisfaction for individuals and the community as a whole</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                <span>Do formal and/or informal surveys to assess individual and community need satisfaction</span>
              </li>
            </ul>
          </div>

          <AIPracticeScenario pageTitle="WELLNESS IN RELATIONSHIPS" pageNumber={0} />
          <PageMessaging pageTitle="WELLNESS IN RELATIONSHIPS" pageNumber={0} />
        </div>
      </div>
    </div>
  );
}
